import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

// Define a consistent color palette
export const PRODUCT_COLOR_PALETTE = ['#71685F', '#33485B', '#9F6460', '#A7674D',
    '#8C7F75', '#688096', '#D4C4C4', '#CE9A85']; // bc palette

// Helper function to get product color by index
export const getProductColor = (index: number): string => {
  return PRODUCT_COLOR_PALETTE[index % PRODUCT_COLOR_PALETTE.length];
};

interface Prediction {
  date: string;
  quantity: Record<number, number>;
}

interface ForecastResponse {
  shop_id: string;
  product_id: string | null;
  season_id: number;
  forecasts: Prediction[];
}

interface ForecastChartProps {
  data: Prediction[] | ForecastResponse[];
  title?: string;
  width?: number;
  height?: number;
  showDisaggregated?: boolean; // New prop to show individual lines
  hoveredProductId?: string | null; // Product ID being hovered
  pinnedProductIds?: string[]; // Product IDs that are pinned (clicked)
  allForecasts?: ForecastResponse[]; // All forecasts for hover functionality
}

export const ForecastChart: React.FC<ForecastChartProps> = ({
  data,
  title = "Forecast",
  width = 800,
  height = 400,
  showDisaggregated = false,
  hoveredProductId = null,
  pinnedProductIds = [],
  allForecasts = []
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(width);

  // Helper function to group data by quarters (every 3 months)
  const groupByQuarter = (predictions: Prediction[]): Prediction[] => {
    if (!predictions || predictions.length === 0) return [];
    
    // Group predictions by quarter
    const quarterGroups = new Map<string, Prediction[]>();
    
    predictions.forEach(pred => {
      const date = new Date(pred.date.split('T')[0]);
      const year = date.getFullYear();
      const month = date.getMonth();
      const quarter = Math.floor(month / 3); // 0-3 for Q1-Q4
      
      // Use the first month of the quarter as the key
      const quarterStartMonth = quarter * 3;
      const quarterKey = `${year}-${String(quarterStartMonth + 1).padStart(2, '0')}-01`;
      
      if (!quarterGroups.has(quarterKey)) {
        quarterGroups.set(quarterKey, []);
      }
      quarterGroups.get(quarterKey)!.push(pred);
    });
    
    // Aggregate data for each quarter
    const result: Prediction[] = [];
    
    quarterGroups.forEach((preds, quarterKey) => {
      // For each quantile, sum the values across all predictions in the quarter
      const aggregatedQuantities: Record<number, number> = {};
      
      preds.forEach(pred => {
        Object.entries(pred.quantity).forEach(([quantile, value]) => {
          const q = Number(quantile);
          if (!aggregatedQuantities[q]) {
            aggregatedQuantities[q] = 0;
          }
          aggregatedQuantities[q] += value;
        });
      });
      
      result.push({
        date: quarterKey,
        quantity: aggregatedQuantities
      });
    });
    
    // Sort by date
    return result.sort((a, b) => a.date.localeCompare(b.date));
  };

  useEffect(() => {
    if (!data || data.length === 0) return;

    // Normalize data - check if it's ForecastResponse[] or Prediction[]
    let normalizedData: Prediction[] = [];
    let individualForecasts: { productId: string; data: Prediction[]; color: string }[] = [];
    let hoveredForecast: { productId: string; data: Prediction[]; color: string } | null = null;
    let pinnedForecasts: { productId: string; data: Prediction[]; color: string }[] = [];
    
    // Check if data is ForecastResponse[] (has 'forecasts' property)
    if ('forecasts' in data[0]) {
      // It's ForecastResponse[], we need to handle individual forecasts
      const forecastResponses = data as ForecastResponse[];
      
      if (showDisaggregated) {
        // Store individual forecasts with colors (grouped by quarter)
        forecastResponses.forEach((fr, index) => {
          if (fr.product_id) {
            individualForecasts.push({
              productId: fr.product_id,
              data: groupByQuarter(fr.forecasts),
              color: getProductColor(index)
            });
          }
        });
      }
      
      // Collect all unique dates
      const allDates = new Set<string>();
      forecastResponses.forEach(fr => {
        fr.forecasts.forEach(pred => {
          allDates.add(pred.date);
        });
      });
      
      // For each date, sum up quantities from all products
      const rawAggregatedData = Array.from(allDates).sort().map(date => {
        const combinedQuantities: Record<number, number> = {};
        
        forecastResponses.forEach(fr => {
          const prediction = fr.forecasts.find(p => p.date === date);
          if (prediction) {
            Object.entries(prediction.quantity).forEach(([quantile, value]) => {
              const q = Number(quantile);
              if (!combinedQuantities[q]) {
                combinedQuantities[q] = 0;
              }
              combinedQuantities[q] += value;
            });
          }
        });
        
        return {
          date: date,
          quantity: combinedQuantities
        };
      });
      
      // Apply quarterly grouping to aggregated data
      normalizedData = groupByQuarter(rawAggregatedData);
    } else {
      // It's already Prediction[] - apply quarterly grouping
      normalizedData = groupByQuarter(data as Prediction[]);
      
      // If we have a hovered product and allForecasts, find it and apply quarterly grouping
      if (hoveredProductId && allForecasts.length > 0) {
        const forecastIndex = allForecasts.findIndex(fr => fr.product_id === hoveredProductId);
        if (forecastIndex !== -1) {
          const fr = allForecasts[forecastIndex];
          hoveredForecast = {
            productId: fr.product_id || '',
            data: groupByQuarter(fr.forecasts),
            color: getProductColor(forecastIndex)
          };
        }
      }
      
      // If we have pinned products and allForecasts, find them and apply quarterly grouping
      if (pinnedProductIds.length > 0 && allForecasts.length > 0) {
        pinnedProductIds.forEach(pinnedId => {
          const forecastIndex = allForecasts.findIndex(fr => fr.product_id === pinnedId);
          if (forecastIndex !== -1) {
            const fr = allForecasts[forecastIndex];
            pinnedForecasts.push({
              productId: fr.product_id || '',
              data: groupByQuarter(fr.forecasts),
              color: getProductColor(forecastIndex)
            });
          }
        });
      }
    }

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove(); // Clear previous chart

    // Get actual container width
    const actualWidth = containerRef.current?.offsetWidth || containerWidth;

    // Set up dimensions and margins
    const margin = { top: 20, right: 30, bottom: 40, left: 50 };
    const innerWidth = actualWidth - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Parse dates and prepare data
    const parseDate = d3.timeParse("%Y-%m-%d");
    const processedData = normalizedData.map(d => ({
      date: parseDate(d.date.split('T')[0]) as Date,
      ...d.quantity
    })).filter(d => d.date !== null);

    if (processedData.length === 0) return;

    // Get all quantiles available in the data
    const allQuantiles = Array.from(
      new Set(normalizedData.flatMap(d => Object.keys(d.quantity).map(Number)))
    ).sort((a, b) => a - b);

    // Set up scales
    const xScale = d3.scaleTime()
      .domain(d3.extent(processedData, d => d.date) as [Date, Date])
      .range([0, innerWidth]);

    // Collect all values for Y scale
    let allValues = processedData.flatMap(d => 
      allQuantiles.map(q => (d as any)[q]).filter(v => v !== undefined)
    );
    
    // If showing disaggregated, also include individual forecast values
    if (showDisaggregated && individualForecasts.length > 0) {
      individualForecasts.forEach(forecast => {
        const forecastProcessedData = forecast.data.map(d => ({
          date: parseDate(d.date.split('T')[0]) as Date,
          ...d.quantity
        })).filter(d => d.date !== null);
        
        const forecastValues = forecastProcessedData.flatMap(d => 
          allQuantiles.map(q => (d as any)[q]).filter(v => v !== undefined)
        );
        
        allValues = [...allValues, ...forecastValues];
      });
    }
    
    // If there's a hovered forecast, include its values too
    if (hoveredForecast) {
      const hoveredProcessedData = hoveredForecast.data.map(d => ({
        date: parseDate(d.date.split('T')[0]) as Date,
        ...d.quantity
      })).filter(d => d.date !== null);
      
      const hoveredValues = hoveredProcessedData.flatMap(d => 
        allQuantiles.map(q => (d as any)[q]).filter(v => v !== undefined)
      );
      
      allValues = [...allValues, ...hoveredValues];
    }
    
    // If there are pinned forecasts, include their values too
    if (pinnedForecasts.length > 0) {
      pinnedForecasts.forEach(pinnedForecast => {
        const pinnedProcessedData = pinnedForecast.data.map(d => ({
          date: parseDate(d.date.split('T')[0]) as Date,
          ...d.quantity
        })).filter(d => d.date !== null);
        
        const pinnedValues = pinnedProcessedData.flatMap(d => 
          allQuantiles.map(q => (d as any)[q]).filter(v => v !== undefined)
        );
        
        allValues = [...allValues, ...pinnedValues];
      });
    }
    
    const yScale = d3.scaleLinear()
      .domain(d3.extent(allValues) as [number, number])
      .nice()
      .range([innerHeight, 0]);

    // Create main group
    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Create line generator
    const line = d3.line<any>()
      .x(d => xScale(d.date))
      .y(d => yScale(d.value));

    // Create area generator for confidence bands
    const area = d3.area<any>()
      .x(d => xScale(d.date))
      .y0(d => yScale(d.lower))
      .y1(d => yScale(d.upper));

    // Define colors for different confidence intervals
    const colors = {
      area: {
        '90': '#e3f2fd', // lightest blue
        '80': '#bbdefb',
        '60': '#90caf9',
        '40': '#64b5f6'
      },
      line: '#1976d2' // main line color
    };

    // Create confidence bands (areas between quantiles)
    const confidenceBands = [
      { lower: 5, upper: 95, opacity: 0.2, color: colors.area['90'] },
      { lower: 10, upper: 90, opacity: 0.3, color: colors.area['80'] },
      { lower: 20, upper: 80, opacity: 0.4, color: colors.area['60'] },
      { lower: 30, upper: 70, opacity: 0.5, color: colors.area['40'] }
    ];

    // Draw confidence bands
    confidenceBands.forEach(band => {
      if (allQuantiles.includes(band.lower) && allQuantiles.includes(band.upper)) {
        const bandData = processedData.map(d => ({
          date: d.date,
          lower: (d as any)[band.lower],
          upper: (d as any)[band.upper]
        })).filter(d => d.lower !== undefined && d.upper !== undefined);

        if (bandData.length > 0) {
          g.append("path")
            .datum(bandData)
            .attr("fill", band.color)
            .attr("opacity", band.opacity)
            .attr("d", area);
        }
      }
    });

    // Placeholder for tooltip functions - will be defined after all lines are drawn
    let showTooltip: (event: any, d: any, productId?: string) => void;
    let hideTooltip: () => void;

    // If showing disaggregated data, draw individual product lines
    if (showDisaggregated && individualForecasts.length > 0) {
      individualForecasts.forEach(forecast => {
        // Process forecast data
        const forecastProcessedData = forecast.data.map(d => ({
          date: parseDate(d.date.split('T')[0]) as Date,
          ...d.quantity
        })).filter(d => d.date !== null);

        // Draw the main line (50th percentile) for this product
        if (allQuantiles.includes(50)) {
          const productLineData = forecastProcessedData.map(d => ({
            date: d.date,
            value: (d as any)[50]
          })).filter(d => d.value !== undefined);

          if (productLineData.length > 0) {
            g.append("path")
              .datum(productLineData)
              .attr("fill", "none")
              .attr("stroke", forecast.color)
              .attr("stroke-width", 2.5)
              .attr("d", line);

            // Add visible dots on the product line
            g.selectAll(`.dot-${forecast.productId}`)
              .data(productLineData)
              .enter().append("circle")
              .attr("class", `dot-${forecast.productId}`)
              .attr("cx", d => xScale(d.date))
              .attr("cy", d => yScale(d.value))
              .attr("r", 3)
              .attr("fill", forecast.color)
              .style("pointer-events", "none");

            // Add invisible larger circles for better hover detection
            g.selectAll(`.dot-hover-${forecast.productId}`)
              .data(productLineData)
              .enter().append("circle")
              .attr("class", `dot-hover-${forecast.productId}`)
              .attr("cx", d => xScale(d.date))
              .attr("cy", d => yScale(d.value))
              .attr("r", 15)
              .attr("fill", "transparent")
              .style("cursor", "pointer")
              .on("mouseover", function(event, d) {
                // Highlight the visible dot
                g.selectAll(`.dot-${forecast.productId}`)
                  .filter((dotData: any) => dotData.date.getTime() === d.date.getTime())
                  .transition()
                  .duration(100)
                  .attr("r", 5);
                showTooltip(event, d, forecast.productId);
              })
              .on("mouseout", function(event, d) {
                // Reset the visible dot
                g.selectAll(`.dot-${forecast.productId}`)
                  .filter((dotData: any) => dotData.date.getTime() === d.date.getTime())
                  .transition()
                  .duration(100)
                  .attr("r", 3);
                hideTooltip();
              });
          }
        }
      });
    } else {
      // Draw the main aggregated line (50th percentile)
      if (allQuantiles.includes(50)) {
        const mainLineData = processedData.map(d => ({
          date: d.date,
          value: (d as any)[50]
        })).filter(d => d.value !== undefined);

        if (mainLineData.length > 0) {
          g.append("path")
            .datum(mainLineData)
            .attr("fill", "none")
            .attr("stroke", colors.line)
            .attr("stroke-width", 2.5)
            .attr("d", line);

          // Add visible dots on the main line
          g.selectAll(".dot")
            .data(mainLineData)
            .enter().append("circle")
            .attr("class", "dot")
            .attr("cx", d => xScale(d.date))
            .attr("cy", d => yScale(d.value))
            .attr("r", 3)
            .attr("fill", colors.line)
            .style("pointer-events", "none");

          // Add invisible larger circles for better hover detection
          g.selectAll(".dot-hover")
            .data(mainLineData)
            .enter().append("circle")
            .attr("class", "dot-hover")
            .attr("cx", d => xScale(d.date))
            .attr("cy", d => yScale(d.value))
            .attr("r", 15)
            .attr("fill", "transparent")
            .style("cursor", "pointer")
            .on("mouseover", function(event, d) {
              // Highlight the visible dot
              g.selectAll(".dot")
                .filter((dotData: any) => dotData.date.getTime() === d.date.getTime())
                .transition()
                .duration(100)
                .attr("r", 5);
              showTooltip(event, d);
            })
            .on("mouseout", function(event, d) {
              // Reset the visible dot
              g.selectAll(".dot")
                .filter((dotData: any) => dotData.date.getTime() === d.date.getTime())
                .transition()
                .duration(100)
                .attr("r", 3);
              hideTooltip();
            });
        }
      }
    }

    // If there are pinned products and we're not in disaggregated mode, draw their lines
    if (pinnedForecasts.length > 0 && !showDisaggregated) {
      pinnedForecasts.forEach(pinnedForecast => {
        const pinnedProcessedData = pinnedForecast.data.map(d => ({
          date: parseDate(d.date.split('T')[0]) as Date,
          ...d.quantity
        })).filter(d => d.date !== null);

        // Draw the pinned product line (50th percentile) - solid line
        if (allQuantiles.includes(50)) {
          const pinnedLineData = pinnedProcessedData.map(d => ({
            date: d.date,
            value: (d as any)[50]
          })).filter(d => d.value !== undefined);

          if (pinnedLineData.length > 0) {
            g.append("path")
              .datum(pinnedLineData)
              .attr("fill", "none")
              .attr("stroke", pinnedForecast.color)
              .attr("stroke-width", 3)
              .attr("d", line);

            // Add visible dots on the pinned product line
            g.selectAll(`.dot-pinned-${pinnedForecast.productId}`)
              .data(pinnedLineData)
              .enter().append("circle")
              .attr("class", `dot-pinned-${pinnedForecast.productId}`)
              .attr("cx", d => xScale(d.date))
              .attr("cy", d => yScale(d.value))
              .attr("r", 4)
              .attr("fill", pinnedForecast.color)
              .attr("stroke", "white")
              .attr("stroke-width", 1.5)
              .style("pointer-events", "none");

            // Add invisible larger circles for better hover detection
            g.selectAll(`.dot-pinned-hover-${pinnedForecast.productId}`)
              .data(pinnedLineData)
              .enter().append("circle")
              .attr("class", `dot-pinned-hover-${pinnedForecast.productId}`)
              .attr("cx", d => xScale(d.date))
              .attr("cy", d => yScale(d.value))
              .attr("r", 15)
              .attr("fill", "transparent")
              .style("cursor", "pointer")
              .on("mouseover", function(event, d) {
                // Highlight the visible dot
                g.selectAll(`.dot-pinned-${pinnedForecast.productId}`)
                  .filter((dotData: any) => dotData.date.getTime() === d.date.getTime())
                  .transition()
                  .duration(100)
                  .attr("r", 6);
                showTooltip(event, d, pinnedForecast.productId);
              })
              .on("mouseout", function(event, d) {
                // Reset the visible dot
                g.selectAll(`.dot-pinned-${pinnedForecast.productId}`)
                  .filter((dotData: any) => dotData.date.getTime() === d.date.getTime())
                  .transition()
                  .duration(100)
                  .attr("r", 4);
                hideTooltip();
              });
          }
        }
      });
    }

    // If there's a hovered product and we're not in disaggregated mode, draw its line
    if (hoveredForecast && !showDisaggregated) {
      const hoveredProcessedData = hoveredForecast.data.map(d => ({
        date: parseDate(d.date.split('T')[0]) as Date,
        ...d.quantity
      })).filter(d => d.date !== null);

      // Draw the hovered product line (50th percentile)
      if (allQuantiles.includes(50)) {
        const hoveredLineData = hoveredProcessedData.map(d => ({
          date: d.date,
          value: (d as any)[50]
        })).filter(d => d.value !== undefined);

        if (hoveredLineData.length > 0) {
          g.append("path")
            .datum(hoveredLineData)
            .attr("fill", "none")
            .attr("stroke", hoveredForecast.color)
            .attr("stroke-width", 3)
            .attr("stroke-dasharray", "5,5")
            .attr("d", line);

          // Add visible dots on the hovered product line
          g.selectAll(`.dot-hovered-${hoveredForecast.productId}`)
            .data(hoveredLineData)
            .enter().append("circle")
            .attr("class", `dot-hovered-${hoveredForecast.productId}`)
            .attr("cx", d => xScale(d.date))
            .attr("cy", d => yScale(d.value))
            .attr("r", 4)
            .attr("fill", hoveredForecast.color)
            .attr("stroke", "white")
            .attr("stroke-width", 1.5)
            .style("pointer-events", "none");

          // Add invisible larger circles for better hover detection
          g.selectAll(`.dot-hovered-hover-${hoveredForecast.productId}`)
            .data(hoveredLineData)
            .enter().append("circle")
            .attr("class", `dot-hovered-hover-${hoveredForecast.productId}`)
            .attr("cx", d => xScale(d.date))
            .attr("cy", d => yScale(d.value))
            .attr("r", 15)
            .attr("fill", "transparent")
            .style("cursor", "pointer")
            .on("mouseover", function(event, d) {
              // Highlight the visible dot
              g.selectAll(`.dot-hovered-${hoveredForecast.productId}`)
                .filter((dotData: any) => dotData.date.getTime() === d.date.getTime())
                .transition()
                .duration(100)
                .attr("r", 6);
              showTooltip(event, d, hoveredForecast.productId);
            })
            .on("mouseout", function(event, d) {
              // Reset the visible dot
              g.selectAll(`.dot-hovered-${hoveredForecast.productId}`)
                .filter((dotData: any) => dotData.date.getTime() === d.date.getTime())
                .transition()
                .duration(100)
                .attr("r", 4);
              hideTooltip();
            });
        }
      }
    }

    // Draw other quantile lines (if 50th percentile is not available, draw all)
    if (!allQuantiles.includes(50)) {
      allQuantiles.forEach((quantile, index) => {
        const lineData = processedData.map(d => ({
          date: d.date,
          value: (d as any)[quantile]
        })).filter(d => d.value !== undefined);

        if (lineData.length > 0) {
          const color = d3.schemeCategory10[index % 10];
          
          g.append("path")
            .datum(lineData)
            .attr("fill", "none")
            .attr("stroke", color)
            .attr("stroke-width", quantile === 50 ? 2.5 : 1.5)
            .attr("stroke-dasharray", quantile === 50 ? "none" : "3,3")
            .attr("d", line);
        }
      });
    }

    // Add vertical grid lines for each quarter (every 3 months)
    const quarterInterval = d3.timeMonth.every(3);
    const quarterTicks = quarterInterval ? xScale.ticks(quarterInterval) : [];
    g.selectAll(".quarter-grid")
      .data(quarterTicks)
      .enter().append("line")
      .attr("class", "quarter-grid")
      .attr("x1", d => xScale(d))
      .attr("x2", d => xScale(d))
      .attr("y1", 0)
      .attr("y2", innerHeight)
      .attr("stroke", "#000000")
      .attr("stroke-width", 2)
      .attr("stroke-dasharray", "3,3")
      .attr("opacity", 1);

    // Add X axis (only ticks and labels, no axis line)
    // Format dates to show quarters
    const formatQuarterDate = (date: Date) => {
      const month = date.getMonth();
      const year = date.getFullYear();
      const quarter = Math.floor(month / 3) + 1; // 1-4 for Q1-Q4
      return `Q${quarter} ${year}`;
    };

    // Get the actual data dates for tick values
    const dataTickValues = processedData.map(d => d.date);

    g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale)
        .tickValues(dataTickValues) // Only show ticks where we have data points
        .tickFormat(d => formatQuarterDate(d as Date))
        .tickSize(0)) // Remove tick lines
      .select(".domain").remove(); // Remove axis line

    // Add Y axis (only ticks and labels, no axis line)
    g.append("g")
      .call(d3.axisLeft(yScale)
        .tickFormat(d => Math.round(d as number).toString()) // Show only integers
        .tickSize(0)) // Remove tick lines
      .select(".domain").remove(); // Remove axis line

    // Create tooltip group AFTER EVERYTHING (including axes) - this ensures it appears on top
    const tooltip = g.append("g")
      .attr("class", "tooltip")
      .style("display", "none");

    // Tooltip background
    const tooltipRect = tooltip.append("rect")
      .attr("fill", "rgba(0, 0, 0, 0.9)")
      .attr("rx", 4)
      .attr("ry", 4);

    // Tooltip text
    const tooltipText = tooltip.append("text")
      .attr("fill", "white")
      .style("font-size", "12px")
      .style("font-weight", "500")
      .attr("text-anchor", "middle");

    // Define tooltip functions now that tooltip elements are created
    showTooltip = (event: any, d: any, productId?: string) => {
      const month = d.date.getMonth();
      const year = d.date.getFullYear();
      const quarter = Math.floor(month / 3) + 1; // 1-4 for Q1-Q4
      const dateStr = `Q${quarter} ${year}`;
      const valueStr = Math.round(d.value).toString();
      
      let text = `${dateStr}: ${valueStr}`;
      if (productId) {
        text = `${productId}\n${text}`;
      }
      
      tooltipText.text("");
      const lines = text.split('\n');
      
      lines.forEach((line, i) => {
        tooltipText.append("tspan")
          .attr("x", 0)
          .attr("dy", i === 0 ? 0 : "1.2em")
          .text(line);
      });
      
      const bbox = (tooltipText.node() as SVGTextElement).getBBox();
      const padding = 8;
      const rectWidth = bbox.width + padding * 2;
      const rectHeight = bbox.height + padding * 2;
      
      tooltipRect
        .attr("x", -rectWidth / 2)
        .attr("y", -rectHeight - padding)
        .attr("width", rectWidth)
        .attr("height", rectHeight);
      
      tooltipText
        .attr("y", -rectHeight / 2 - padding / 2);
      
      // Position tooltip near the point, but keep it inside the chart
      let xPos = xScale(d.date);
      let yPos = yScale(d.value) - rectHeight - 8;
      
      // Keep tooltip inside chart horizontally
      if (xPos - rectWidth / 2 < 0) {
        xPos = rectWidth / 2;
      } else if (xPos + rectWidth / 2 > innerWidth) {
        xPos = innerWidth - rectWidth / 2;
      }
      
      // Keep tooltip inside chart vertically
      if (yPos < 0) {
        yPos = yScale(d.value) + 12; // Show below the point instead
      }
      
      tooltip
        .attr("transform", `translate(${xPos},${yPos})`)
        .style("display", null);
    };

    hideTooltip = () => {
      tooltip.style("display", "none");
    };

  }, [data, width, height, title, showDisaggregated, hoveredProductId, pinnedProductIds, allForecasts, containerWidth]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    let resizeTimeout: ReturnType<typeof setTimeout>;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 150);
    };

    // Set initial width
    handleResize();

    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  return (
    <div ref={containerRef} className="w-full">
      <svg
        ref={svgRef}
        width="100%"
        height={height}
        className="bg-white"
        viewBox={`0 0 ${containerRef.current?.offsetWidth || width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
      />
    </div>
  );
};

export default ForecastChart;
